import styles from "./last-days.module.css";

import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
} from "recharts";
import moment from "moment";
import { useTranslation } from "react-i18next";
import AveragesComponent from "./averages-component/AveragesComponent";
import { inject, observer } from "mobx-react";
import { ClientDashboardStore } from "../../store/store";

const barProps = {
    stackId: "same-bar",
    barSize: 13,
};

export enum StoryStatusStringKey {
    "outOfRoomCount",
    "inBathroomCount",
    "inRoomCount",
    "inBedCount",
    "outOfBedCount",
}

export interface IDayData {
    day: string;
    hoursInBed: number;
    hoursOutOfRoom: number;
    hoursInRoom: number;
    hoursInBathroom: number;
}

export interface ITimeData {
    day: string;
    timesOutOfRoom: number;
    timesVisitedBathroom: number;
    timesOutOfBed: number;
}

export interface LastDaysPanelProps {
    hoursData: IDayData[];
    timesData: ITimeData[];
    isSevenDaysView: boolean;
    clientDashboardStore?: ClientDashboardStore;
}

function LastDaysPanel(props: LastDaysPanelProps) {
    const { t } = useTranslation("dashboard");
    const clientDashboardStore = props.clientDashboardStore!;
    const { historicalTab, periodStatistics } = clientDashboardStore;

    const render = () => {
        setBarSize();
        return (
            <div className={styles["last-days"]}>
                <div className={styles["column"]}>
                    <span className={styles["chart-title"]}>{t("Hours")}</span>
                    {hours()}
                </div>
                <div className={styles["column"]}>{times()}</div>
            </div>
        );
    };

    const setBarSize = () => {
        if (props.isSevenDaysView) {
            barProps.barSize = 26;
        } else if (window.innerWidth > 1366) {
            barProps.barSize = 13;
        } else {
            barProps.barSize = 8;
        }
    };

    const hours = () => {
        const { hoursData } = props;
        let itemsData: any[] = hoursData;

        if (itemsData.length === 0) {
            itemsData = [];

            for (let i = 7; i >= 1; i--) {
                const today = moment().seconds(0).minutes(0).hours(0);
                itemsData.push({ day: today.subtract(i, "d").format("D") });
            }
        }

        let OtherHoursCount = 0;
        let OutOfBedHoursCount = 0;

        itemsData.map((dayData) => {
            OtherHoursCount += dayData.hoursOther;
            OutOfBedHoursCount += dayData.hoursOutOfBed;
        });

        return (
            <ResponsiveContainer height={255} width="99%">
                <BarChart
                    data={itemsData}
                    margin={{ top: 20, right: 26, left: -24, bottom: 5 }}
                    barCategoryGap={2}
                >
                    <CartesianGrid stroke="#b7b7b7" vertical={false} />
                    <XAxis dataKey="day" interval={0} tickCount={1} />
                    <YAxis
                        type="number"
                        domain={[0, 24]}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(val: number) =>
                            Math.round(val).toString()
                        }
                    />
                    {OtherHoursCount > 0 && (
                        <Bar
                            dataKey="hoursOther"
                            name={t("Hours in other statuses")}
                            fill="#87A5B4"
                            stroke="#fff"
                            isAnimationActive={false}
                            {...barProps}
                        />
                    )}
                    {OutOfBedHoursCount > 0 && (
                        <Bar
                            dataKey="hoursOutOfBed"
                            name={t("Hours out of bed")}
                            fill="#7EBBBE"
                            stroke="#fff"
                            isAnimationActive={false}
                            {...barProps}
                        />
                    )}
                    <Bar
                        dataKey="hoursInBed"
                        name={t("Hours in bed")}
                        fill="#064F58"
                        stroke="#fff"
                        {...barProps}
                        isAnimationActive={false}
                    />
                    <Bar
                        dataKey="hoursInRoom"
                        name={t("Hours in room")}
                        fill="#52D9E3"
                        stroke="#fff"
                        isAnimationActive={false}
                        {...barProps}
                    />
                    <Bar
                        dataKey="hoursInBathroom"
                        name={t("Hours in bathroom")}
                        fill="#2476B9"
                        stroke="#fff"
                        isAnimationActive={false}
                        {...barProps}
                    />
                    <Bar
                        dataKey="hoursOutOfRoom"
                        name={t("Hours out of room")}
                        fill="#868483"
                        stroke="#fff"
                        isAnimationActive={false}
                        {...barProps}
                    />

                    <Legend layout="vertical" content={legendContent} />
                </BarChart>
            </ResponsiveContainer>
        );
    };

    const times = () => {
        const { timesData } = props;

        let itemsData: any[] = timesData;

        if (itemsData.length === 0) {
            itemsData = [];

            for (let i = 7; i >= 1; i--) {
                const today = moment().seconds(0).minutes(0).hours(0);
                itemsData.push({ day: today.subtract(i, "d").format("D") });
            }
        }
        return (
            <div className={styles["main-panel"]}>
                {dailySummary()}
                {multipleDaySummary()}
            </div>
        );
    };

    const dailySummary = () => {
        return (
            <div className={styles["daily-panel"]}>
                <div className={styles["panel-title"]}>
                    {t("DailyResidentStory")}
                </div>
                <div className={styles["panel-content"]}>
                    {dailySummaryContent()}
                    <div className={styles["count-panel"]}>
                        {dailyCountSummaryContent()}
                    </div>
                </div>
            </div>
        );
    };

    const multipleDaySummary = () => {
        return (
            <div className={styles["multiple-day-panel"]}>
                <div className={styles["panel-title"]}>
                    {t("MultipleDayResidentStory")}
                </div>
                <div className={styles["panel-content"]}>
                    {multipleDaySummayContent()}
                    <div className={styles["count-panel"]}>
                        {multipleDayCountSummayContent()}
                    </div>
                </div>

                <AveragesComponent />
            </div>
        );
    };

    const getDaysText = () => {
        if (historicalTab === 0) {
            return t("Last 7 days").toLowerCase();
        }
        return t("Last 30 days").toLowerCase();
    };

    const dailySummaryContent = () => {
        let translatedText = "";

        if (periodStatistics.daily.hours.Other === 0) {
            translatedText = t("DailyPanelContent");
        } else {
            translatedText = t("DailyPanelContentOther");
        }

        translatedText = translatedText.replace(
            "{{outOfRoomCount}}",
            `<span style="color: blue">${periodStatistics.daily.hours[
                "OutOfRoom"
            ].toFixed(1)} ${t("Hours").toLowerCase()}</span>`
        );

        translatedText = translatedText.replace(
            "{{inBathroomCount}}",
            `<span style="color: blue">${periodStatistics.daily.hours[
                "InBathroom"
            ].toFixed(1)} ${t("Hours").toLowerCase()}</span>`
        );

        translatedText = translatedText.replace(
            "{{inRoomCount}}",
            `<span style="color: blue">${periodStatistics.daily.hours[
                "InRoom"
            ].toFixed(1)} ${t("Hours").toLowerCase()}</span>`
        );

        translatedText = translatedText.replace(
            "{{inBedCount}}",
            `<span style="color: blue">${periodStatistics.daily.hours[
                "InBed"
            ].toFixed(1)} ${t("Hours").toLowerCase()}</span>`
        );

        translatedText = translatedText.replace(
            "{{inOther}}",
            `<span style="color: blue">${getPeriodStatisticHours("Other")} ${t(
                "Hours"
            ).toLowerCase()}</span>`
        );

        return <div dangerouslySetInnerHTML={{ __html: translatedText }}></div>;
    };

    const dailyCountSummaryContent = () => {
        let translatedText = "";

        if (periodStatistics.daily.count.Other === 0) {
            translatedText = t("DailyCountPanelConent");
        } else {
            translatedText = t("DailyCountPanelConentOther");
        }

        translatedText = translatedText.replace(
            "{{outOfBedCount}}",
            `<span style="color: blue">${t("Out of bed").toLocaleLowerCase()} ${
                periodStatistics.daily.count["OutOfBed"]
            } ${t("Times").toLocaleLowerCase()}</span>`
        );

        translatedText = translatedText.replace(
            "{{inBathroomCount}}",
            `<span style="color: blue">${t("ToBathroom").toLocaleLowerCase()} ${
                periodStatistics.daily.count["InBathroom"]
            } ${t("Times").toLocaleLowerCase()}</span>`
        );

        translatedText = translatedText.replace(
            "{{outOfRoomCount}}",
            `<span style="color: blue">${t("LeftRoom").toLowerCase()} ${
                periodStatistics.daily.count["OutOfRoom"]
            } ${t("Times").toLocaleLowerCase()}</span>`
        );

        translatedText = translatedText.replace(
            "{{otherCount}}",
            `<span style="color: blue">${t("OtherStatuses").toLowerCase()} ${
                periodStatistics.daily.count["Other"]
            } ${t("Times").toLocaleLowerCase()}</span>`
        );

        return <div dangerouslySetInnerHTML={{ __html: translatedText }}></div>;
    };

    const getPeriodStatisticHours = (statusType: string) => {
        if (historicalTab === 0) {
            return (
                (periodStatistics.sevenDays.hours as any)[statusType] / 7
            ).toFixed(1);
        }

        return (
            (periodStatistics.thirtyDays.hours as any)[statusType] / 30
        ).toFixed(1);
    };

    const getPeriodStatisticTimes = (statusType: string) => {
        if (historicalTab === 0) {
            return (
                (periodStatistics.sevenDays.count as any)[statusType] / 7
            ).toFixed(1);
        }

        return (
            (periodStatistics.thirtyDays.count as any)[statusType] / 30
        ).toFixed(1);
    };

    const multipleDaySummayContent = () => {
        let translatedText = "";

        if (Number(getPeriodStatisticHours("Other")) === 0) {
            translatedText = t("MultipleDayPanelContent");
        } else {
            translatedText = t("MultipleDayPanelContentOther");
        }

        translatedText = translatedText.replace(
            "{{daysCount}}",
            `<span style="color: blue">${getDaysText()}</span>`
        );

        translatedText = translatedText.replace(
            "{{outOfRoomCount}}",
            `<span style="color: blue">${getPeriodStatisticHours(
                "OutOfRoom"
            )} ${t("Hours").toLowerCase()}</span>`
        );

        translatedText = translatedText.replace(
            "{{inBathroomCount}}",
            `<span style="color: blue">${getPeriodStatisticHours(
                "InBathroom"
            )} ${t("Hours").toLowerCase()}</span>`
        );

        translatedText = translatedText.replace(
            "{{inRoomCount}}",
            `<span style="color: blue">${getPeriodStatisticHours("InRoom")} ${t(
                "Hours"
            ).toLowerCase()}</span>`
        );

        translatedText = translatedText.replace(
            "{{inBedCount}}",
            `<span style="color: blue">${getPeriodStatisticHours("InBed")} ${t(
                "Hours"
            ).toLowerCase()}</span>`
        );

        translatedText = translatedText.replace(
            "{{inOther}}",
            `<span style="color: blue">${getPeriodStatisticHours("Other")} ${t(
                "Hours"
            ).toLowerCase()}</span>`
        );

        return <div dangerouslySetInnerHTML={{ __html: translatedText }}></div>;
    };

    const multipleDayCountSummayContent = () => {
        let translatedText = "";

        if (Number(getPeriodStatisticTimes("Other")) === 0) {
            translatedText = t("MultipleDaysCountPanelConent");
        } else {
            translatedText = t("MultipleDaysCountPanelConentOther");
        }

        translatedText = translatedText.replace(
            "{{outOfBedCount}}",
            `<span style="color: blue">${t(
                "Out of bed"
            ).toLocaleLowerCase()} ${getPeriodStatisticTimes("OutOfBed")} ${t(
                "Times"
            ).toLocaleLowerCase()} ${t("PerDay")}</span>`
        );

        translatedText = translatedText.replace(
            "{{inBathroomCount}}",
            `<span style="color: blue">${t(
                "ToBathroom"
            ).toLocaleLowerCase()} ${getPeriodStatisticTimes("InBathroom")} ${t(
                "Times"
            ).toLocaleLowerCase()} ${t("PerDay")}</span>`
        );

        translatedText = translatedText.replace(
            "{{outOfRoomCount}}",
            `<span style="color: blue">${t(
                "LeftRoom"
            ).toLowerCase()} ${getPeriodStatisticTimes("OutOfRoom")} ${t(
                "Times"
            ).toLocaleLowerCase()} ${t("PerDay")}</span>`
        );

        translatedText = translatedText.replace(
            "{{otherCount}}",
            `<span style="color: blue">${t(
                "OtherStatuses"
            ).toLowerCase()} ${getPeriodStatisticTimes("Other")} ${t(
                "Times"
            ).toLocaleLowerCase()} ${t("PerDay")}</span>`
        );

        return <div dangerouslySetInnerHTML={{ __html: translatedText }}></div>;
    };

    const legendContent = (props: any) => {
        const { payload } = props;
        
        return (
            <ul className={styles["recharts-default-legend"]}>
                {payload.reverse().map(legendItem)}
            </ul>
        );
    };

    const legendItem = (item: any, index: any) => (
        <li
            key={`legend-item-${index}`}
            className={styles["recharts-legend-item"]}
        >
            <svg
                width="14"
                height="14"
                viewBox="0 0 32 32"
                version="1.1"
                style={{
                    display: "inline-block",
                    verticalAlign: "middle",
                    marginRight: "4px",
                }}
            >
                <path
                    stroke="none"
                    fill={item.color}
                    d="M0,4h32v24h-32z"
                ></path>
            </svg>
            <span
                style={{
                    color: item.color,
                }}
            >
                {item.value}
            </span>
        </li>
    );

    return render();
}

export default inject("clientDashboardStore")(observer(LastDaysPanel));
